/** Labels and tones for insurance enums (English source strings; translate with t()). */
import { formatNumber } from '@/lib/format';
import { msg, t } from '@/lib/i18n';
import type { Tone } from '@/ui';

type Label = { label: string; tone: Tone };

/** Risk bands of the risk-v1 model (higher index = riskier). */
export const BAND: Record<string, Label & { range: string }> = {
  low: { label: msg('Low'), tone: 'good', range: '0–25' },
  moderate: { label: msg('Moderate'), tone: 'info', range: '25–45' },
  elevated: { label: msg('Elevated'), tone: 'medium', range: '45–65' },
  high: { label: msg('High'), tone: 'bad', range: '65–100' },
};
export const BANDS = ['low', 'moderate', 'elevated', 'high'] as const;

/** Model components by key, so their names translate (the server sends English labels). */
export const COMPONENT: Record<string, { label: string; description: string; unobservable: string }> = {
  vehicle_control: {
    label: msg('Harsh driving'),
    description: msg('Harsh acceleration, braking and cornering per 100 km of observable distance.'),
    unobservable: msg('Needs trusted harsh-event data (accelerometer); too little in this period.'),
  },
  speed_management: {
    label: msg('Speeding'),
    description: msg('Time over the limit, weighted by how far over, per speed-limit band.'),
    unobservable: msg('Needs driving on roads with a known speed limit; too little in this period.'),
  },
  road_sharing: {
    label: msg('Following distance'),
    description: msg('Share of moving time too close to the vehicle ahead, where headway is monitored.'),
    unobservable: msg('Needs headway monitoring; not fitted, not trusted or too little in this period.'),
  },
  attention: {
    label: msg('Attention'),
    description: msg('Distraction, phone use, eyes closed and yawning per 100 km with a working driver camera.'),
    unobservable: msg('Needs a working driver camera; not fitted, not trusted or too little in this period.'),
  },
  collision_warnings: {
    label: msg('Collision warnings'),
    description: msg('Forward and pedestrian collision warnings per 100 km; 5 per 100 km is maximum risk.'),
    unobservable: msg('Only where a collision-warning system is fitted.'),
  },
  night_driving: {
    label: msg('Night driving'),
    description: msg('Share of driving between 22:00 and 06:00; 50 % or more is maximum risk.'),
    unobservable: msg('Needs driving in this period.'),
  },
};

/** Event rates in the insurer's exposure slice (per 100 km). */
export const EVENT_RATE: Record<string, string> = {
  harsh: msg('Harsh events per 100 km'),
  speeding: msg('Speeding events per 100 km'),
  collision_warnings: msg('Collision warnings per 100 km'),
  attention: msg('Attention events per 100 km'),
};

export const CLAIM_STATUS: Record<string, Label> = {
  open: { label: msg('Open'), tone: 'info' },
  submitted: { label: msg('Submitted'), tone: 'accent' },
  accepted: { label: msg('Accepted'), tone: 'good' },
  rejected: { label: msg('Declined'), tone: 'bad' },
  closed: { label: msg('Closed'), tone: 'neutral' },
};

export const CLAIM_TYPE: Record<string, string> = {
  collision: msg('Collision'),
  theft: msg('Theft'),
  vandalism: msg('Vandalism'),
  glass: msg('Glass'),
  weather: msg('Weather'),
  fire: msg('Fire'),
  injury: msg('Injury'),
  cargo: msg('Cargo'),
  other: msg('Other'),
};

export const CLAIM_SOURCE: Record<string, string> = {
  manual: msg('Entered by hand'),
  fnol: msg('Reported from an event'),
  api: msg('API'),
  agent: msg('Assistant'),
  flow: msg('Automation'),
};

export const EVIDENCE_KIND: Record<string, string> = {
  detection: msg('Event'),
  trip: msg('Trip'),
  video: msg('Video'),
  file: msg('File'),
  link: msg('Link'),
};

export function labelOf(map: Record<string, { label: string } | string>, key: string | null | undefined): string {
  if (!key) return '—';
  const v = map[key];
  if (!v) return key.replace(/_/g, ' ');
  return t(typeof v === 'string' ? v : v.label);
}

export function componentLabel(key: string, fallback?: string): string {
  const c = COMPONENT[key];
  return c ? t(c.label) : (fallback ?? key.replace(/_/g, ' '));
}

/** Risk points → tone, the same cut-offs as the bands. */
export function riskTone(v: number | null | undefined): Tone {
  if (v == null) return 'neutral';
  return v < 25 ? 'good' : v < 45 ? 'info' : v < 65 ? 'medium' : 'bad';
}

export function formatEur(v: number | null | undefined): string {
  return v == null ? '—' : `€ ${formatNumber(v, 0)}`;
}
